// TerminationContainer.tsx
import React, { useState } from "react";
import { Referral, Roles } from "types";
import { updateAssetReferral } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useAuth } from "context/AuthContext";
import ReadMoreText from "components/ReadMoreText";
import FileRow from "components/file-components/file-row";
import { hasRole } from "utils/helper";
import FileViewer from "components/file-components/fileViewer2";
import EditIcon from "components/icon/EditIcon";
import { useTranslation } from "react-i18next";

interface TerminationContainerProps {
  referralId: string;
  referral: Referral;
  setActiveTab?: (tab: string) => void;
  onTerminationCreated: (res: any) => void;
  handleCreateTerminationRequestClick?: () => void;
}

const TerminationContainer: React.FC<TerminationContainerProps> = ({
  referralId,
  referral,
  setActiveTab,
  onTerminationCreated,
  handleCreateTerminationRequestClick,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [loading, setLoading] = useState(false);

  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);

  // Extract termination data from referral
  const termination_reason = referral?.termination_reason;
  const termination_date = referral?.termination_date;
  const termination_order_document = referral?.termination_order_documents;
  const is_termination_requested = Boolean(
    referral?.termination_reason && referral?.termination_date,
  );
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };
  const handleApproveTermination = async () => {
    if (!referralId) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action_status", "Terminated By Prosecutor");

      const response = await updateAssetReferral(referralId, formData);

      showTooltip(t("termination_request_approved_successfully"), "success");
      if (onTerminationCreated && response) {
        onTerminationCreated(response);
        setActiveTab?.("Terminate");
      }
    } catch (error) {
      console.error("Failed to approve termination:", error);
      showTooltip(t("failed_to_approve_termination_request"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectTermination = async () => {
    if (!referralId) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action_status", "Termination Rejected");
      const response = await updateAssetReferral(referralId, formData);

      showTooltip(t("termination_request_rejected_successfully"), "success");
      if (onTerminationCreated && response) {
        onTerminationCreated(response);
        setActiveTab?.("Terminate");
      }
    } catch (error) {
      console.error("Failed to reject termination:", error);
      showTooltip(t("failed_to_reject_termination_request"), "error");
    } finally {
      setLoading(false);
    }
  };

  const canCoordinatorTakeAction =
    isAssetCoordinator && referral?.action_status === "Termination Requested";

  return (
    <div className="">
      {/* Termination Request Section */}
      {is_termination_requested && (
        <div className="shadow-md rounded-md p-4">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Termination Request
              </h2>
              {isAssetProsecutor &&
                referral?.action_status === "Termination Requested" &&
                is_termination_requested && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCreateTerminationRequestClick?.();
                      console.log("Edit Termination Request");
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors"
                  >
                    <EditIcon />
                    Edit
                  </button>
                )}
              {termination_date && (
                <span className="text-sm text-gray-600 font-bold">
                  {new Date(termination_date).toLocaleDateString()}
                </span>
              )}
            </div>

            <div className="flex justify-between items-start flex-col mt-4">
              <div className="flex flex-col w-full">
                {/* <label className="mb-2 text-base font-semibold text-gray-800">
                    Termination Reason
                  </label> */}
                <div className="py-2">
                  {termination_reason ? (
                    <p className="text-gray-700 whitespace-pre-wrap">
                      <ReadMoreText text={termination_reason} maxLines={3} />
                    </p>
                  ) : (
                    <p className="text-gray-400">
                      No termination reason provided
                    </p>
                  )}
                </div>
              </div>

              {/* Termination Order Documents */}
              {termination_order_document &&
                termination_order_document.length > 0 && (
                  <div className="w-full mt-4">
                    <label className="mb-2 text-base font-semibold text-gray-800">
                      Termination Order Documents
                    </label>
                    <div className="space-y-2">
                      {termination_order_document.map(
                        (file: any, index: number) => (
                          <FileRow
                            file={file}
                            key={file.document_uuid || index}
                            w="w-full"
                            {...fileRowProps}
                          />
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {canCoordinatorTakeAction && (
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleRejectTermination}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {loading ? "Processing..." : "Reject Termination"}
              </button>

              <button
                onClick={handleApproveTermination}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-700 disabled:opacity-60 transition-colors"
              >
                {loading ? "Processing..." : "Approve Termination"}
              </button>
            </div>
          )}
        </div>
      )}

      <FileViewer
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={() => setIsFileViewerOpen(false)}
        selectedApplicantDocument={selectedDocument}
      />
    </div>
  );
};

export default TerminationContainer;
